/** DTOs of the collaboration API (snake_case, exactly as the backend returns them). */
import type { BoardAppearance } from '@/entities/appearance';
import type { MemberRole } from '@/entities/workspace';

export type { MemberRole };

export const MEMBER_ROLES: readonly MemberRole[] = [
  'owner',
  'lead_moderator',
  'moderator',
  'editor',
  'commenter',
  'member',
];
/** Roles that can be handed out (the owner moves only by transfer). */
export const ASSIGNABLE_ROLES: readonly MemberRole[] = MEMBER_ROLES.filter((r) => r !== 'owner');

export interface WorkspaceMember {
  user_id: string;
  display_name: string;
  login: string;
  /** The standard role, or `null` while a custom role is assigned (one role per member). */
  role: MemberRole | null;
  custom_role_id: string | null;
  team_ids: string[];
  joined_at: string;
}

export interface CustomRole {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  rank: number;
  permissions: string[];
  /** Default board permissions of the holders (a template, never access by itself). */
  board_permissions: string[];
  member_count: number;
  created_at: string;
}

/** One horizontal level of the workspace role hierarchy (``owner``, ``std:<role>``, ``lvl:<id>``). */
export interface HierarchyLevel {
  key: string;
  rank: number;
  standard_role: MemberRole | null;
  protected: boolean;
  custom_role_ids: string[];
  member_count: number;
  removable: boolean;
}

export interface RoleHierarchy {
  revision: number;
  levels: HierarchyLevel[];
  can_edit: boolean;
}

export interface HierarchyLayoutLevel {
  key: string;
  custom_role_ids: string[];
}

export interface HierarchyChange {
  role_id: string;
  name: string;
  from_rank: number;
  to_rank: number;
  member_count: number;
}

export interface HierarchyResult {
  dry_run: boolean;
  changes: HierarchyChange[];
  added_levels: number;
  removed_levels: number;
  hierarchy: RoleHierarchy;
}

export interface MatrixRole {
  role: MemberRole;
  permissions: string[];
  defaults: string[];
  editable: boolean;
}

export interface PermissionMatrix {
  roles: MatrixRole[];
  groups: Record<string, string[]>;
  owner_only: string[];
  /** Only the workspace owner may add these to a role or hand out a role carrying them. */
  owner_assigned?: string[];
  required: string[];
}

export interface Team {
  id: string;
  name: string;
  description: string | null;
  member_ids: string[];
  /** The designated leader (an active member of the team); decides board succession. */
  leader_user_id: string | null;
  rule_count: number;
  created_at: string;
}

export interface BoardLabelEntry {
  id: string;
  name: string;
  color: string;
  board_count: number;
  access_controlling: boolean;
  /** Catalog order (presentation only; never access). */
  position: number;
  created_at: string;
}

export type RuleSubjectKind = 'team' | 'role' | 'custom_role';

export interface AccessRule {
  id: string;
  subject_kind: RuleSubjectKind;
  team_id: string | null;
  subject_role: MemberRole | null;
  custom_role_id: string | null;
  granted_role: MemberRole;
  board_id: string | null;
  board_name: string | null;
  label_ids: string[];
  label_match: 'any' | 'all';
  created_at: string;
}

export interface ImpactItem {
  user_id: string;
  display_name: string;
  board_id: string;
  board_name: string;
  change: 'gained' | 'lost' | 'changed';
  role_before: MemberRole | null;
  role_after: MemberRole | null;
}

export interface Impact {
  gained: number;
  lost: number;
  changed: number;
  hidden_boards: number;
  items: ImpactItem[];
}

export interface Participant {
  user_id: string;
  display_name: string;
  role: MemberRole;
  /** Empty and `is_workspace_member: false` for everybody when the viewer is a board-only guest. */
  sources: string[];
  is_workspace_member: boolean;
  is_board_owner: boolean;
}

export interface BoardOwnership {
  owner_user_id: string;
  owner_name: string;
  created_by_user_id: string | null;
  created_by_name: string | null;
  origin_team_id: string | null;
  origin_team_name: string | null;
  can_manage: boolean;
  can_revoke: boolean;
  succession: {
    user_id: string;
    display_name: string;
    step: 'origin_team' | 'first_team' | 'default_owner' | 'workspace_owner';
  } | null;
}

export interface ExplicitMember {
  user_id: string;
  display_name: string;
  role: MemberRole;
  source: string;
  is_workspace_member: boolean;
  created_at: string;
}

export interface BoardAccess {
  members: ExplicitMember[];
  denials: { user_id: string; display_name: string; created_at: string }[];
  exceptions: { user_id: string; display_name: string; granted: string[]; revoked: string[] }[];
  role_overrides: Record<string, Record<string, boolean>>;
  rules: AccessRule[];
  label_ids: string[];
  transfer_restricted: boolean;
}

/** What an invitee can already do in a database (database invitations): accepting adds a DIRECT
 * role, which is authoritative over a role inherited from rules. */
export interface InvitationCurrentAccess {
  role: MemberRole | null;
  sources: string[];
  can_view: boolean;
}

export interface Invitation {
  id: string;
  scope: 'workspace' | 'board' | 'database';
  workspace_id: string | null;
  board_id: string | null;
  database_id?: string | null;
  current_access?: InvitationCurrentAccess | null;
  target_name: string;
  invitee_user_id: string;
  invitee_display_name: string;
  inviter_user_id: string | null;
  inviter_display_name: string | null;
  /** NULL for a custom workspace role (``custom_role_id`` / ``custom_role_name``). */
  role: MemberRole | null;
  custom_role_id: string | null;
  custom_role_name: string | null;
  status: string;
  expires_at: string;
  created_at: string;
}

export interface InviteLink {
  id: string;
  scope: 'workspace' | 'board';
  role: MemberRole | null;
  custom_role_id: string | null;
  custom_role_name: string | null;
  requires_approval: boolean;
  expires_at: string;
  max_uses: number;
  uses_count: number;
  remaining_uses: number;
  created_by_user_id: string | null;
  created_by_display_name: string | null;
  created_at: string;
}

export interface LinkPreview {
  scope: 'workspace' | 'board';
  target_name: string;
  role: MemberRole | null;
  custom_role_name: string | null;
  requires_approval: boolean;
  expires_at: string;
  already_participant: boolean;
  /** This account was rejected through this link: it cannot ask again. */
  rejected: boolean;
}

export interface RedeemResult {
  outcome: 'joined' | 'pending_approval' | 'already_participant';
  scope: 'workspace' | 'board' | 'database';
  workspace_id: string | null;
  board_id: string | null;
  database_id?: string | null;
}

export type RequestKind = 'join_workspace' | 'join_board' | 'board_transfer';

export interface AccessRequest {
  id: string;
  kind: RequestKind;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired';
  requester: { user_id: string; display_name: string };
  workspace_id: string | null;
  board_id: string | null;
  board_name: string | null;
  payload: Record<string, unknown>;
  /** Board transfers: the two explicit approvals (`workspace_id` is the destination). */
  source_workspace_id: string | null;
  source_approved: boolean;
  destination_approved: boolean;
  new_owner_user_id: string | null;
  expires_at: string | null;
  can_approve_source: boolean;
  can_approve_destination: boolean;
  created_at: string;
  resolved_at: string | null;
  resolved_by: { user_id: string; display_name: string } | null;
}

export interface AuditEvent {
  id: string;
  event_type: string;
  actor_user_id: string | null;
  actor_name: string | null;
  subject_user_id: string | null;
  board_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface AppNotification {
  id: string;
  type: string;
  actor: { user_id: string; display_name: string } | null;
  workspace_id: string | null;
  board_id: string | null;
  card_id: string | null;
  entity_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
}

export interface NotificationPage {
  items: AppNotification[];
  next_cursor: string | null;
  unread_count: number;
}

export interface BoardLabelBrief {
  id: string;
  name: string;
  color: string;
}

export interface AccessibleBoard {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  appearance: BoardAppearance | null;
  background_image_url: string | null;
  is_favorite: boolean;
  /** The board's own last edit (rename, look ...) - NOT activity on its cards. */
  updated_at: string;
  /** The newest activity the viewer may see on the board; `null` = nothing recorded. */
  last_activity_at: string | null;
  created_at: string;
  role: MemberRole;
  /** `owned`: the viewer is this board's owner; `shared`: invited / team / administrative. */
  relation: 'owned' | 'shared';
  owner_user_id: string;
  workspace_id: string | null;
  workspace_name: string | null;
  workspace_type: 'personal' | 'team' | null;
  labels: BoardLabelBrief[];
  created_by_name: string | null;
  owner_name: string | null;
  transfer_restricted: boolean;
  /** Offered control only: the server decides (and an access-granting label needs more). */
  can_edit_labels: boolean;
  /** Offered control only (the server decides): the viewer may restyle / rename this board. */
  can_edit_details: boolean;
}

export interface Person {
  user_id: string;
  display_name: string;
}

/** A mentionable or mentioned person: ``login`` (unique) is what follows ``@`` in the text. */
export interface Mention extends Person {
  login: string;
}

export interface CardComment {
  id: string;
  card_id: string;
  author: Person | null;
  body: string;
  mentions: Mention[];
  created_at: string;
  edited_at: string | null;
  can_edit: boolean;
  can_delete: boolean;
  /** The top-level comment of the thread; `null` = this IS a top-level comment. */
  thread_root_id: string | null;
  /** The comment answered and the person addressed (replies only). */
  parent_id: string | null;
  reply_to: Person | null;
  /** A placeholder that keeps the replies of a deleted comment (no text, no author). */
  deleted: boolean;
}

export interface WatchState {
  watching: boolean;
  watchers: Person[];
}

export interface TransferImpact {
  destination_workspace_id: string;
  destination_name: string;
  same_owner: boolean;
  requires_approval: boolean;
  source_requires_approval: boolean;
  destination_requires_approval: boolean;
  owner_after_user_id: string | null;
  owner_after_name: string | null;
  participants_losing_access: { user_id: string; display_name: string; role: MemberRole }[];
  access_rules_removed: number;
  board_labels_removed: number;
  invitations_revoked: number;
  links_revoked: number;
  denials_removed: number;
}

export interface TransferResult {
  status: 'completed' | 'pending_approval';
  pending_source: boolean;
  pending_destination: boolean;
  request_id: string | null;
  workspace_id: string | null;
}

export interface NotificationPolicyCategory {
  id: string;
  enabled: boolean;
  default: boolean;
}

export interface NotificationPolicy {
  categories: NotificationPolicyCategory[];
  can_manage: boolean;
}

export interface NotificationPreferences {
  categories: {
    id: string;
    workspace_enabled: boolean;
    enabled: boolean;
    can_receive: boolean;
  }[];
  mandatory: string[];
}

export interface DeletedWorkspace {
  id: string;
  name: string;
  deleted_at: string;
  purge_after: string;
  owner_user_id: string | null;
  owner_name: string | null;
  deleted_by_name: string | null;
  board_count: number;
}

/** A task assignment being composed (creation forms). */
export interface AssignmentDraft {
  kind: 'unassigned' | 'assigned' | 'shared';
  ids: string[];
}

export const NO_ASSIGNMENT: AssignmentDraft = { kind: 'unassigned', ids: [] };
